import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Papa from "papaparse";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import IssuerLayout from "@/components/IssuerLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IssueCertificates = () => {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [manualData, setManualData] = useState({
    name: "",
    email: "",
    completion_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    fetchCertificate();
  }, [certId]);

  const fetchCertificate = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/certificates/${certId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificate(response.data);
    } catch (err) {
      console.error('Failed to fetch certificate:', err);
      if (err.response?.status === 404) {
        toast.error("Certificate not found");
        navigate("/issuer/certificates");
      } else {
        toast.error("Failed to load certificate");
      }
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    setLoading(true);
    
    // Use Papa Parse for robust CSV parsing
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: async (results) => {
        if (results.errors.length > 0) {
          console.error('CSV parsing errors:', results.errors);
          toast.error(`CSV parsing error: ${results.errors[0].message}`);
          setLoading(false);
          return;
        }

        const recipients = results.data
          .filter(row => row.name && row.email)
          .map(row => ({
            name: (row.name || row.Name || '').trim(),
            email: (row.email || row.Email || '').trim(),
            completion_date: row.completion_date || row.date || row.Date || new Date().toISOString().split('T')[0]
          }));

        if (recipients.length === 0) {
          toast.error("No valid recipients found in CSV. Make sure you have 'name' and 'email' columns.");
          setLoading(false);
          return;
        }

        try {
          const token = localStorage.getItem('issuer_token');
          await axios.post(`${API}/recipients/bulk`, {
            certificate_id: certId,
            recipients
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          toast.success(`${recipients.length} certificates issued successfully!`);
          navigate("/issuer/certificates");
        } catch (err) {
          console.error('Bulk upload error:', err);
          
          let errorMessage = "Failed to issue certificates";
          
          if (err.response?.data?.detail) {
            const detail = err.response.data.detail;
            
            if (Array.isArray(detail)) {
              const messages = detail.map(e => {
                if (typeof e === 'string') return e;
                if (e.msg) return e.msg;
                if (e.message) return e.message;
                return 'Validation error';
              });
              errorMessage = messages.join('. ');
            }
            else if (detail.message) {
              errorMessage = detail.message;
            }
            else if (typeof detail === 'string') {
              errorMessage = detail;
            }
          }
          
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      },
      error: (error) => {
        console.error('Papa Parse error:', error);
        toast.error(`Failed to parse CSV: ${error.message}`);
        setLoading(false);
      }
    });
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('issuer_token');
      await axios.post(`${API}/recipients/single?certificate_id=${certId}`, manualData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Certificate issued successfully!");
      navigate("/issuer/certificates");
    } catch (err) {
      console.error('Manual issue error:', err);
      
      // Extract user-friendly error message
      let errorMessage = "Failed to issue certificate";
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // Handle array of validation errors
        if (Array.isArray(detail)) {
          const messages = detail.map(e => {
            if (typeof e === 'string') return e;
            if (e.msg) return e.msg;
            if (e.message) return e.message;
            return 'Validation error';
          });
          errorMessage = messages.join('. ');
        }
        // Handle object with message property (free tier limit)
        else if (detail.message) {
          errorMessage = detail.message;
        }
        // Handle string detail
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!certificate) {
    return (
      <IssuerLayout>
        <div className="flex items-center justify-center h-64" data-testid="loading-certificate">
          <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
        </div>
      </IssuerLayout>
    );
  }

  return (
    <IssuerLayout>
      <div className="max-w-4xl" data-testid="issue-certificates-page">
        <div className="mb-8">
          <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Issue Certificates</h1>
          <p className="text-slate-600">{certificate.title}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <Tabs defaultValue="bulk" className="w-full" data-testid="issue-tabs">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="bulk" data-testid="bulk-tab">Bulk Upload (CSV)</TabsTrigger>
              <TabsTrigger value="manual" data-testid="manual-tab">Manual Entry</TabsTrigger>
            </TabsList>

            <TabsContent value="bulk" data-testid="bulk-content">
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">CSV Format</Label>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">
                    <p className="text-slate-600 mb-2">Your CSV should have these columns:</p>
                    <code className="text-slate-900 font-mono">name, email, completion_date</code>
                    <p className="text-slate-500 mt-2 text-xs">Example: John Doe, john@example.com, 2025-01-15</p>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Upload CSV File</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      className="h-11 rounded-lg border-slate-200"
                      data-testid="csv-file-input"
                    />
                    {csvFile && <span className="text-sm text-slate-600" data-testid="csv-filename">{csvFile.name}</span>}
                  </div>
                </div>

                <Button
                  onClick={handleCsvUpload}
                  disabled={loading || !csvFile}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-3 font-medium"
                  data-testid="bulk-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Issue Certificates</>
                  )}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="manual" data-testid="manual-content">
              <form onSubmit={handleManualSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">Recipient Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={manualData.name}
                    onChange={(e) => setManualData({...manualData, name: e.target.value})}
                    placeholder="John Doe"
                    required
                    className="h-11 rounded-lg border-slate-200"
                    data-testid="manual-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={manualData.email}
                    onChange={(e) => setManualData({...manualData, email: e.target.value})}
                    placeholder="john@example.com"
                    required
                    className="h-11 rounded-lg border-slate-200"
                    data-testid="manual-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="completion_date" className="text-sm font-medium text-slate-700 mb-1.5 block">Completion Date</Label>
                  <Input
                    id="completion_date"
                    type="date"
                    value={manualData.completion_date}
                    onChange={(e) => setManualData({...manualData, completion_date: e.target.value})}
                    required
                    className="h-11 rounded-lg border-slate-200"
                    data-testid="manual-date-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-3 font-medium"
                  data-testid="manual-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <><Plus className="h-4 w-4 mr-2" /> Issue Certificate</>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </IssuerLayout>
  );
};

export default IssueCertificates;
